import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ArrowRight2,
  BagCross,
  Box1,
  Call,
  Clock,
  Copy,
  Link1,
  Location,
  Printer,
  RecordCircle,
  TickCircle,
} from "iconsax-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import type { GetOrdersStatus } from "@/api/schemas/getOrdersStatus";
import { UpdateOrderStatusDtoStatus } from "@/api/schemas/updateOrderStatusDtoStatus";
import { ProductIconTile, SefanoIconButton, StatusBadge } from "@/components/Atoms";
import { SefanoButton } from "@/components/Molecules";
import { PageTemplate } from "@/components/Templates/Page";
import { orderStatusDisplayMap } from "@/lib/dashboard/orderStatus";
import { formatPersianCurrency, formatPersianNumber } from "@/lib/format/persian";
import type { OrderDetailTemplateProps } from "./OrderDetail.template.types";
import * as styles from "./OrderDetail.template.styles";

export type { OrderDetailTemplateProps } from "./OrderDetail.template.types";

function getInitial(name: string): string {
  return name.trim().charAt(0) || "—";
}

function formatAmount(value: number): string {
  return formatPersianCurrency(value).replace(" ریال", "");
}

export function OrderDetailTemplate({
  order,
  customer,
  paymentLink,
  timeline,
  isLoading,
  isError,
  isUpdatingStatus,
  updateStatus,
  formatOrderDate,
  formatOrderDateShort,
  formatCustomerSince,
}: OrderDetailTemplateProps) {
  const { t } = useTranslation(["orders", "home", "common"]);
  const theme = useTheme();
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);

  const handleStatusMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusMenuAnchor(null);
  }, []);

  const handleStatusSelect = useCallback(
    (status: GetOrdersStatus) => {
      updateStatus(status);
      setStatusMenuAnchor(null);
    },
    [updateStatus],
  );

  const handleCopyLink = useCallback(() => {
    if (!paymentLink) return;
    void navigator.clipboard.writeText(paymentLink);
  }, [paymentLink]);

  if (isLoading) {
    return (
      <PageTemplate maxWidth={1440}>
        <Box sx={styles.loadingWrap}>
          <CircularProgress />
        </Box>
      </PageTemplate>
    );
  }

  if (isError || !order) {
    return (
      <PageTemplate maxWidth={1440}>
        <Box sx={styles.notFoundCard}>
          <Box sx={styles.notFoundIconWrap}>
            <BagCross size={60} variant="Bulk" color={theme.palette.error.main} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {t("orders:detail.notFoundTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380, lineHeight: 1.8 }}>
            {t("orders:detail.notFoundDescription")}
          </Typography>
          <Box sx={{ mt: 1.75 }}>
            <Link to="/orders" style={{ textDecoration: "none" }}>
              <SefanoButton>{t("orders:detail.backToOrders")}</SefanoButton>
            </Link>
          </Box>
        </Box>

        <Box sx={styles.mobileNotFound}>
          <Box sx={styles.mobileNotFoundIconWrap}>
            <BagCross size={52} variant="Bulk" color={theme.palette.error.main} />
          </Box>
          <Typography sx={styles.mobileNotFoundTitle}>{t("orders:detail.notFoundTitle")}</Typography>
          <Typography sx={styles.mobileNotFoundDescription}>
            {t("orders:detail.mobileNotFoundDescription")}
          </Typography>
          <Link to="/orders" style={{ textDecoration: "none" }}>
            <SefanoButton sx={styles.mobileNotFoundButton}>{t("orders:detail.backToOrders")}</SefanoButton>
          </Link>
        </Box>
      </PageTemplate>
    );
  }

  const statusDisplay = orderStatusDisplayMap[order.status];
  const customerName = customer?.full_name ?? "—";
  const itemsSubtotal = order.total_amount ?? 0;
  const isPendingPayment = order.status === "created";
  const orderNumberLabel = `#${order.order_number ?? order.id}`;
  const statusOptions = Object.values(UpdateOrderStatusDtoStatus);

  return (
    <PageTemplate maxWidth={1440}>
      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopTopSection}>
          <Link to="/orders" style={{ textDecoration: "none" }}>
            <Typography component="span" sx={styles.backLink}>
              <ArrowRight2 size={17} variant="Linear" />
              {t("orders:detail.backToOrders")}
            </Typography>
          </Link>

          <Box sx={styles.titleRow}>
            <Box sx={styles.titleGroup}>
              <Typography sx={styles.orderTitle}>
                {t("orders:detail.orderLabel")} {orderNumberLabel}
              </Typography>
              <StatusBadge
                color={statusDisplay.color}
                size="md"
                label={t(`home:orders.status.${order.status}`)}
              />
              <Typography sx={styles.createdAt}>
                {t("orders:detail.registeredAt", {
                  date: formatOrderDate(order.created_at),
                })}
              </Typography>
            </Box>
            <Box sx={styles.actionsRow}>
              <Box component="button" type="button" sx={styles.ghostButton}>
                <Printer size={18} variant="Linear" />
                {t("orders:detail.printInvoice")}
              </Box>
              <SefanoButton
                sx={styles.statusButton}
                disabled={isUpdatingStatus}
                onClick={handleStatusMenuOpen}
              >
                {t("orders:detail.updateStatus")}
              </SefanoButton>
              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {statusOptions.map((status) => (
                  <MenuItem
                    key={status}
                    selected={status === order.status}
                    disabled={isUpdatingStatus}
                    onClick={() => handleStatusSelect(status)}
                    sx={styles.statusMenuItem}
                  >
                    {t(`home:orders.status.${status}`)}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.mobileHeaderBar}>
          <Link to="/orders" style={{ textDecoration: "none" }}>
            <SefanoIconButton variant="plain" sx={styles.mobileIconButton} aria-label={t("orders:detail.backToOrders")}>
              <ArrowRight2 size={19} variant="Linear" />
            </SefanoIconButton>
          </Link>
          <Box sx={styles.mobileHeaderText}>
            <Typography sx={styles.mobileHeaderTitle}>
              {t("orders:detail.orderLabel")} {orderNumberLabel}
            </Typography>
            <Typography sx={styles.mobileHeaderDate}>{formatOrderDateShort(order.created_at)}</Typography>
          </Box>
          <SefanoIconButton variant="plain" sx={styles.mobileIconButton} aria-label={t("orders:detail.printInvoice")}>
            <Printer size={19} variant="Linear" />
          </SefanoIconButton>
        </Box>

        <Box sx={styles.mobileStatusBanner}>
          <Typography sx={styles.paymentBannerText}>{t("orders:detail.orderStatus")}</Typography>
          <StatusBadge
            color={statusDisplay.color}
            size="sm"
            label={t(`home:orders.status.${order.status}`)}
          />
        </Box>

        <Box sx={styles.desktopLayout}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={styles.card}>
              <Box sx={styles.cardHeader}>
                <Box1 size={19} variant="Bold" color={theme.palette.primary.main} />
                <Typography sx={styles.cardTitle}>{t("orders:detail.items")}</Typography>
              </Box>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.th}>{t("orders:detail.productColumn")}</TableCell>
                    <TableCell sx={styles.th} align="center">
                      {t("orders:detail.quantityColumn")}
                    </TableCell>
                    <TableCell sx={styles.th}>{t("orders:detail.unitPriceColumn")}</TableCell>
                    <TableCell sx={styles.th}>{t("orders:detail.lineTotalColumn")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell sx={styles.td}>
                        <Box sx={styles.productCell}>
                          <ProductIconTile />
                          <Typography fontWeight={600}>{item.product_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.td} align="center">
                        ×{formatPersianNumber(item.quantity)}
                      </TableCell>
                      <TableCell sx={{ ...styles.td, color: "text.secondary" }}>
                        {formatAmount(item.unit_price)}
                      </TableCell>
                      <TableCell sx={{ ...styles.td, fontWeight: 600 }}>
                        {formatAmount(item.unit_price * item.quantity)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box sx={styles.card}>
              <Box sx={styles.cardHeader}>
                <Clock size={19} variant="Bold" color={theme.palette.primary.main} />
                <Typography sx={styles.cardTitle}>{t("orders:detail.timeline")}</Typography>
              </Box>
              <Box>
                {timeline.map((entry, index) => {
                  const isDone = entry.state === "done";
                  const isCurrent = entry.state === "current";
                  const dotBg = isDone
                    ? theme.palette.primary.main
                    : isCurrent
                      ? alpha(theme.palette.warning.main, 0.15)
                      : theme.palette.background.default;
                  const dotBorder = isDone
                    ? theme.palette.primary.main
                    : isCurrent
                      ? theme.palette.warning.main
                      : theme.palette.divider;
                  const lineColor = isDone ? theme.palette.primary.main : theme.palette.divider;
                  const titleColor = isDone || isCurrent ? "text.primary" : "text.disabled";

                  return (
                    <Box key={`${entry.title}-${index}`} sx={styles.timelineItem}>
                      <Box sx={styles.timelineRail}>
                        <Box sx={styles.timelineDot(dotBg, dotBorder)}>
                          {isDone ? (
                            <TickCircle size={16} variant="Bold" color="#fff" />
                          ) : isCurrent ? (
                            <Clock size={16} variant="Bold" color={theme.palette.warning.main} />
                          ) : (
                            <RecordCircle
                              size={16}
                              variant="Linear"
                              color={theme.palette.text.disabled}
                            />
                          )}
                        </Box>
                        {index < timeline.length - 1 ? (
                          <Box sx={styles.timelineLine(lineColor)} />
                        ) : null}
                      </Box>
                      <Box sx={styles.timelineContent}>
                        <Typography sx={styles.timelineTitle(titleColor)}>{entry.title}</Typography>
                        <Typography sx={styles.timelineTime}>{entry.time}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={styles.customerCard}>
              <Typography sx={styles.sidebarCardTitle}>{t("orders:detail.customerInfo")}</Typography>
              <Box sx={styles.profileRow}>
                <Box sx={styles.avatar}>{getInitial(customerName)}</Box>
                <Box>
                  <Typography fontWeight={600}>{customerName}</Typography>
                  {customer?.created_at ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("orders:detail.customerSince", {
                        date: formatCustomerSince(customer.created_at),
                      })}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Box sx={styles.contactList}>
                {customer?.phone_number ? (
                  <Box sx={styles.contactRow}>
                    <Call size={17} variant="Linear" color={theme.palette.primary.main} />
                    <Typography sx={{ direction: "ltr", textAlign: "start" }}>
                      {customer.phone_number}
                    </Typography>
                  </Box>
                ) : null}
                {customer?.address || order.shipping_address ? (
                  <Box sx={styles.contactRow}>
                    <Location size={17} variant="Linear" color={theme.palette.primary.main} />
                    <Typography>{customer?.address ?? order.shipping_address}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>

            <Box sx={styles.customerCard}>
              <Typography sx={styles.sidebarCardTitle}>{t("orders:detail.paymentSummary")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.375 }}>
                <Box sx={styles.paymentRow}>
                  <Typography sx={styles.paymentLabel}>{t("orders:detail.itemsSubtotal")}</Typography>
                  <Typography sx={styles.paymentValue}>{formatAmount(itemsSubtotal)}</Typography>
                </Box>
                <Box sx={styles.paymentRow}>
                  <Typography sx={styles.paymentLabel}>{t("orders:detail.shippingCost")}</Typography>
                  <Typography sx={styles.paymentValue}>
                    {formatAmount(order.shipping_cost ?? 0)}
                  </Typography>
                </Box>
                <Box sx={styles.paymentRow}>
                  <Typography sx={styles.paymentLabel}>{t("orders:detail.discount")}</Typography>
                  <Typography sx={{ ...styles.paymentValue, color: "success.main" }}>
                    {formatPersianNumber(order.discount_amount ?? 0)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.paymentTotalRow}>
                <Typography sx={styles.paymentTotalLabel}>{t("orders:detail.totalAmount")}</Typography>
                <Typography sx={styles.paymentTotalValue}>
                  {formatAmount(order.final_payable_amount)}{" "}
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "text.secondary" }}
                  >
                    {t("create.currency")}
                  </Typography>
                </Typography>
              </Box>
              {isPendingPayment ? (
                <Box sx={styles.paymentBanner}>
                  <Clock size={18} variant="Bold" color={theme.palette.warning.main} />
                  <Typography sx={styles.paymentBannerText}>
                    {t("orders:detail.awaitingCustomerPayment")}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box sx={styles.sideActions}>
              <SefanoButton
                intent="primary"
                fullWidth
                startIcon={<Link1 size={18} variant="Linear" color="#fff" />}
              >
                {t("orders:detail.resendPaymentLink")}
              </SefanoButton>
              <Box
                component="button"
                type="button"
                sx={styles.copyLinkButton}
                onClick={handleCopyLink}
                disabled={!paymentLink}
              >
                <Copy size={17} variant="Linear" />
                {t("orders:detail.copyOrderLink")}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.mobileStack}>
          <Box sx={styles.mobileCard}>
            <Box sx={styles.mobileCustomerRow}>
              <Box sx={styles.mobileAvatar}>{getInitial(customerName)}</Box>
              <Box>
                <Typography fontWeight={600} fontSize="0.875rem">
                  {customerName}
                </Typography>
                {customer?.phone_number ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ direction: "ltr", display: "block", textAlign: "start" }}
                  >
                    {customer.phone_number}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            {customer?.address || order.shipping_address ? (
              <Box sx={styles.mobileAddressRow}>
                <Location size={16} variant="Linear" color={theme.palette.primary.main} />
                <Typography>{customer?.address ?? order.shipping_address}</Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={styles.mobileCard}>
            <Typography sx={styles.mobileCardTitle}>{t("orders:detail.items")}</Typography>
            {order.items.map((item) => (
              <Box key={item.id} sx={styles.mobileItemRow}>
                <ProductIconTile size={40} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} fontSize="0.8125rem">
                    {item.product_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatAmount(item.unit_price)} × {formatPersianNumber(item.quantity)}
                  </Typography>
                </Box>
                <Typography fontWeight={600} fontSize="0.8125rem" flexShrink={0}>
                  {formatAmount(item.unit_price * item.quantity)}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={styles.mobileCard}>
            <Typography sx={styles.mobileCardTitle}>{t("orders:detail.paymentSummary")}</Typography>
            <Box sx={styles.mobilePaymentRow}>
              <Typography color="text.secondary">{t("orders:detail.itemsSubtotal")}</Typography>
              <Typography fontWeight={500}>{formatAmount(itemsSubtotal)}</Typography>
            </Box>
            <Box sx={styles.mobilePaymentRow}>
              <Typography color="text.secondary">{t("orders:detail.shippingCost")}</Typography>
              <Typography fontWeight={500}>{formatAmount(order.shipping_cost ?? 0)}</Typography>
            </Box>
            <Box sx={styles.mobilePaymentTotalRow}>
              <Typography sx={styles.mobilePaymentTotalLabel}>{t("orders:detail.totalAmount")}</Typography>
              <Typography sx={styles.mobilePaymentTotalValue}>
                {formatAmount(order.final_payable_amount)}{" "}
                <Typography
                  component="span"
                  sx={{ fontSize: "0.625rem", fontWeight: 500, color: "text.secondary" }}
                >
                  {t("create.currency")}
                </Typography>
              </Typography>
            </Box>
          </Box>

          <SefanoButton
            intent="primary"
            fullWidth
            startIcon={<Link1 size={18} variant="Linear" color="#fff" />}
            sx={styles.mobileResendButton}
          >
            {t("orders:detail.resendPaymentLink")}
          </SefanoButton>
        </Box>
      </Box>
    </PageTemplate>
  );
}
